import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mantine/core";
import type { DocumentViewResponse } from "../../types/reportTemplate";
import {
  captureReportHtml,
  collectPageStylesheets,
} from "../../utils/reportHtml";
import { DocumentView } from "./DocumentView";
import { LegacyHistoricalReportUnavailable } from "./LegacyHistoricalReportUnavailable";

export interface LegacyHistoricalReportCompletion {
  pageCount: number;
}

interface Props {
  caseId: string;
  documentViewData: DocumentViewResponse;
  onReady: (completion: LegacyHistoricalReportCompletion) => void;
  onPending: () => void;
  onError: () => void;
}

export function LegacyHistoricalReportPreview({
  caseId,
  documentViewData,
  onReady,
  onPending,
  onError,
}: Props) {
  const root = useRef<HTMLDivElement>(null);
  const generation = useRef(0);
  const [failed, setFailed] = useState(false);
  const caseData = useMemo(
    () => ({
      id: caseId,
      caseNumber: documentViewData.legacyBaseline?.header.caseNumber || "",
      isFinalized: false,
    }),
    [caseId, documentViewData]
  );
  const emptyMap = useMemo(() => new Map(), []);
  const handlePending = useCallback(() => {
    generation.current += 1;
    setFailed(false);
    onPending();
  }, [onPending]);
  const handleError = useCallback(() => {
    generation.current += 1;
    setFailed(true);
    onError();
  }, [onError]);
  const handleSettled = useCallback(
    async (pageCount: number) => {
      const current = generation.current;
      if (!root.current) return;
      try {
        await captureReportHtml(root.current, {
          pageStylesheets: collectPageStylesheets(),
        });
        if (generation.current === current) onReady({ pageCount });
      } catch {
        if (generation.current === current) handleError();
      }
    },
    [handleError, onReady]
  );
  useEffect(
    () => () => {
      generation.current += 1;
    },
    []
  );
  if (!documentViewData.legacyBaseline)
    return <LegacyHistoricalReportUnavailable onError={handleError} />;
  return (
    <Box
      ref={root}
      style={{ overflowX: "auto" }}
      data-legacy-historical-preview="true"
    >
      {failed && (
        <Alert color="red" title="Report preview incomplete" role="alert">
          The complete report and its preserved graphics must load before this
          reconstruction can be reviewed. Reload the saved draft to try again.
        </Alert>
      )}
      <DocumentView
        caseData={caseData}
        specimens={[]}
        diagnosisDataMap={emptyMap}
        grossingDataMap={emptyMap}
        clinicalHistoryNotes=""
        indications=""
        impressions=""
        pathologistComment=""
        documentViewDataOverride={documentViewData}
        isReadOnly
        paginate
        noScrollWrapper
        onPaginationPending={handlePending}
        onPaginationSettled={handleSettled}
        onPaginationFailed={handleError}
      />
    </Box>
  );
}
